import type { DeviceRebootResult, DeviceStatusResponse } from '@/types/api-client/device-tools'
import { getSeverityStateVariant, STATE_VARIANTS } from '@/utils/equipment/severityColors'
import type { Ref } from 'vue'

/**
 * Resolves a test value with fallback to actual value
 * Used to simplify test/production logic in device tool components
 *
 * @param testValue - Ref containing test value (null when not testing)
 * @param actualValue - The actual production value to use as fallback
 * @returns Test value if set, otherwise actual value
 *
 * @example
 * const isLoading = computed(() => resolveTestValue(testIsLoading, state.value?.loading || false))
 */
export function resolveTestValue<T>(testValue: Ref<T | null>, actualValue: T): T {
	return testValue.value !== null ? testValue.value : actualValue
}

/**
 * Mock device status responses for testing
 * Colors are derived from severity using STATE_VARIANTS
 */
export const DEVICE_STATUS_MOCKS = [
	{
		id: 'healthy',
		label: 'Healthy',
		description: 'Device is healthy with no issues',
		data: [{
			severity: 'info',
			message: 'No alarms found',
			alarmStatus: false,
			deviceOnline: true,
			deviceType: 'ONT',
			deviceBrand: 'Calix',
			fullDetails: {
				description: 'Fiber Optical Network Terminal',
				firmwareVersion: '2.4.1',
				uptime: '15d 4h 23m',
				temperature: '42°C',
				signalStrength: '-23dBm',
				networkStatus: 'Connected',
				config: {
					wifi: {
						ssid: 'MyNetwork-5G',
						channel: 149,
						bandwidth: '80MHz'
					}
				}
			}
		}] as DeviceStatusResponse[]
	},
	{
		id: 'healthy-ok',
		label: 'OK',
		description: 'Device status is OK',
		data: [{
			severity: 'ok',
			message: 'All systems operational',
			alarmStatus: false,
			deviceOnline: true,
			deviceType: 'Router',
			deviceBrand: 'UISP',
			fullDetails: {
				model: 'EdgeRouter X',
				firmwareVersion: 'v2.0.9',
				uptime: '45d 12h 5m',
				cpuLoad: '12%',
				memoryUsage: '34%',
				interfaces: {
					wan: 'Connected - 1000Mbps',
					lan: 'Active - 4 devices',
					wifi: 'Enabled - 12 clients'
				}
			}
		}] as DeviceStatusResponse[]
	},
	{
		id: 'warning',
		label: 'Warning',
		description: 'Device has minor issues',
		data: [{
			severity: 'warning',
			message: 'Signal strength below optimal',
			alarmStatus: false,
			deviceOnline: true,
			deviceType: 'ONT',
			deviceBrand: 'Calix',
			fullDetails: {
				description: 'Fiber ONT - Performance Degraded',
				signalStrength: '-28dBm',
				optimalRange: '-15dBm to -25dBm',
				temperature: '48°C',
				lastOptimization: '2024-01-15 14:30:00',
				diagnostics: {
					rxPower: '-28.5dBm',
					txPower: '2.3dBm',
					packetLoss: '0.2%'
				}
			}
		}] as DeviceStatusResponse[]
	},
	{
		id: 'minor',
		label: 'Minor',
		description: 'Minor issues detected',
		data: [{
			severity: 'minor',
			message: 'Performance degraded',
			alarmStatus: false,
			deviceOnline: true,
			deviceType: 'ONT',
			deviceBrand: 'Calix',
			fullDetails: null
		}] as DeviceStatusResponse[]
	},
	{
		id: 'alarm',
		label: 'Alarm',
		description: 'Alarm is active',
		data: [{
			severity: 'warning',
			message: 'High temperature detected',
			alarmStatus: true,
			deviceOnline: true,
			deviceType: 'ONT',
			deviceBrand: 'Calix',
			fullDetails: null
		}] as DeviceStatusResponse[]
	},
	{
		id: 'critical',
		label: 'Critical',
		description: 'Critical issues require immediate attention',
		data: [{
			severity: 'critical',
			message: 'Hardware failure detected',
			alarmStatus: true,
			deviceOnline: true,
			deviceType: 'ONT',
			deviceBrand: 'Calix',
			fullDetails: {
				description: 'Critical Hardware Failure',
				errorCode: 'HW-FAULT-003',
				temperature: '68°C',
				temperatureThreshold: '55°C',
				signalStrength: '-35dBm',
				lastReboot: '2024-01-20 09:15:33',
				alarms: ['TEMP_HIGH', 'SIGNAL_LOSS', 'FAN_FAILURE'],
				diagnostics: {
					powerSupply: 'degraded',
					opticalModule: 'failing',
					fanSpeed: '0 RPM'
				}
			}
		}] as DeviceStatusResponse[]
	},
	{
		id: 'major',
		label: 'Major',
		description: 'Major issue affecting service',
		data: [{
			severity: 'major',
			message: 'Connection instability',
			alarmStatus: true,
			deviceOnline: true,
			deviceType: 'Router',
			deviceBrand: 'UISP',
			fullDetails: null
		}] as DeviceStatusResponse[]
	},
	{
		id: 'error',
		label: 'Error',
		description: 'Error state',
		data: [{
			severity: 'error',
			message: 'Communication failure',
			alarmStatus: false,
			deviceOnline: true,
			deviceType: 'ONT',
			deviceBrand: 'Calix',
			fullDetails: null
		}] as DeviceStatusResponse[]
	},
	{
		id: 'offline',
		label: 'Offline',
		description: 'Device is offline',
		data: [{
			severity: 'critical',
			message: 'Device unreachable',
			alarmStatus: false,
			deviceOnline: false,
			deviceType: 'ONT',
			deviceBrand: 'Calix',
			fullDetails: null
		}] as DeviceStatusResponse[]
	},
	// UISP-specific mocks - UISP uses nested status fields instead of deviceOnline
	// Note: severity is set to effective value for test panel color preview (actual API returns 'n/a')
	{
		id: 'uisp-online',
		label: 'UISP Online',
		description: 'UISP device that is active (status in nested fullDetails)',
		data: [{
			severity: 'ok', // Effective severity for color mapping (API returns 'n/a')
			message: 'Device status retrieved, last seen: 2025-12-01T16:25:04.904Z',
			alarmStatus: false,
			deviceOnline: true,
			deviceType: 'UFiber Instant',
			deviceBrand: 'Uisp',
			fullDetails: {
				identification: {
					site: {
						id: '4f77-a64-49-885-8df8a969',
						name: 'Customer Name',
						status: 'active',
						type: 'endpoint'
					},
					mac: '70:a7:41:36:ff:fc',
					model: 'UF-Instant',
					modelName: 'UFiber Instant',
					firmwareVersion: '4.9.3',
					type: 'onu',
					category: 'optical'
				},
				overview: {
					status: 'active',
					cpu: 0,
					ram: 41,
					signal: -17,
					uptime: 10713191,
					distance: 3999,
					lastSeen: '2025-12-01T16:25:04.904Z',
					voltage: 3,
					biasCurrent: 24
				}
			}
		}] as DeviceStatusResponse[]
	},
	{
		id: 'uisp-offline',
		label: 'UISP Offline',
		description: 'UISP device that is disconnected (deviceOnline is misleadingly true)',
		data: [{
			severity: 'disconnected', // Effective severity for color mapping (API returns 'n/a')
			message: 'Device status retrieved, last seen: 2025-02-03T01:45:57.451Z',
			alarmStatus: false,
			deviceOnline: true, // Note: UISP returns true even when device is disconnected!
			deviceType: 'EdgeRouter X SFP',
			deviceBrand: 'Uisp',
			fullDetails: {
				identification: {
					site: {
						id: '378237sd1-sdfsdf723sd',
						name: 'Customer Name',
						status: 'disconnected',
						type: 'endpoint'
					},
					mac: '79:x7:91:x8:7x:99',
					name: 'cr-0787',
					hostname: 'cr-0787',
					model: 'ER-X-SFP',
					modelName: 'EdgeRouter X SFP',
					firmwareVersion: '2.0.9-hotfix.6-mx',
					type: 'erouter',
					category: 'wired'
				},
				overview: {
					status: 'disconnected',
					lastSeen: '2025-02-03T01:45:57.451Z'
				}
			}
		}] as DeviceStatusResponse[]
	},
	{
		id: 'unknown',
		label: 'Unknown',
		description: 'Unknown severity type',
		data: [{
			severity: 'unknown',
			message: 'Status indeterminate',
			alarmStatus: false,
			deviceOnline: true,
			deviceType: 'Unknown',
			deviceBrand: 'Generic',
			fullDetails: null
		}] as DeviceStatusResponse[]
	},
	{
		id: 'ready',
		label: 'Ready',
		description: 'Ready to check status (initial state)',
		data: [{
			severity: 'default',
			message: 'Ready to check device status',
			alarmStatus: false,
			deviceOnline: true,
			deviceType: 'ONT',
			deviceBrand: 'Calix',
			fullDetails: null
		}] as DeviceStatusResponse[]
	},
	// STATE VARIANTS - For testing loading/cooldown states
	{
		id: 'loading',
		label: 'Checking',
		description: 'Status check in progress',
		data: [{
			severity: 'info',
			message: 'Checking device status...',
			alarmStatus: false,
			deviceOnline: true,
			deviceType: 'ONT',
			deviceBrand: 'Calix',
			fullDetails: null
		}] as DeviceStatusResponse[]
	},
	{
		id: 'cooldown',
		label: 'Cooldown (Success)',
		description: 'In cooldown period after successful status check',
		data: [{
			severity: 'ok',
			message: 'Status check complete',
			alarmStatus: false,
			deviceOnline: true,
			deviceType: 'ONT',
			deviceBrand: 'Calix',
			fullDetails: null
		}] as DeviceStatusResponse[]
	},
	{
		id: 'cooldown-warning',
		label: 'Cooldown (Warning)',
		description: 'In cooldown period after status check with warnings',
		data: [{
			severity: 'warning',
			message: 'Status check complete - minor issues detected',
			alarmStatus: false,
			deviceOnline: true,
			deviceType: 'ONT',
			deviceBrand: 'Calix',
			fullDetails: null
		}] as DeviceStatusResponse[]
	},
	{
		id: 'cooldown-error',
		label: 'Cooldown (Error)',
		description: 'In cooldown period after failed status check',
		data: [{
			severity: 'error',
			message: 'Status check failed',
			alarmStatus: true,
			deviceOnline: true,
			deviceType: 'ONT',
			deviceBrand: 'Calix',
			fullDetails: null
		}] as DeviceStatusResponse[]
	}
]

/**
 * Get variant groups for device status testing
 * Organized into Results and States sections
 * Colors are automatically derived from severity using STATE_VARIANTS
 */
export function getDeviceStatusVariantGroups() {
	// Results section - completed status checks and initial state
	const resultMocks = ['healthy', 'healthy-ok', 'warning', 'minor', 'alarm', 'critical', 'major', 'error', 'offline', 'uisp-online', 'uisp-offline', 'unknown', 'ready']
	const results = DEVICE_STATUS_MOCKS
		.filter(mock => resultMocks.includes(mock.id))
		.map(mock => {
			const severity = mock.data[0]?.severity
			const variantKey = getSeverityStateVariant(severity)
			const variant = STATE_VARIANTS[variantKey]

			return {
				id: mock.id,
				label: mock.label,
				colorClass: variant.colorClass
			}
		})

	// States section - in-progress and special edge cases
	const stateMocks = ['loading', 'cooldown', 'cooldown-warning', 'cooldown-error']
	const states = DEVICE_STATUS_MOCKS
		.filter(mock => stateMocks.includes(mock.id))
		.map(mock => {
			const severity = mock.data[0]?.severity
			const variantKey = getSeverityStateVariant(severity)
			const variant = STATE_VARIANTS[variantKey]

			return {
				id: mock.id,
				label: mock.label,
				colorClass: variant.colorClass
			}
		})

	return [
		{
			label: 'Results',
			variants: results
		},
		{
			label: 'States',
			variants: states
		}
	]
}

/**
 * Mock device reboot results for testing
 * Organized by Results (completed states) and States (in-progress/special states)
 */
export const DEVICE_REBOOT_MOCKS = [
	// RESULTS - Completed reboot operations
	{
		id: 'success',
		label: 'Success',
		description: 'Reboot completed successfully',
		data: {
			isLoading: false,
			success: true,
			hasError: false,
			error: null,
			progress: 100,
			finished: true,
			cooldownSeconds: 0,
			severity: 'ok'
		} as DeviceRebootResult
	},
	{
		id: 'error',
		label: 'Error',
		description: 'Reboot failed with error',
		data: {
			isLoading: false,
			success: false,
			hasError: true,
			error: new Error('Reboot failed'),
			progress: 0,
			finished: true,
			cooldownSeconds: 0,
			severity: 'error'
		} as DeviceRebootResult
	},
	{
		id: 'ready',
		label: 'Ready',
		description: 'Ready to reboot (default state)',
		data: {
			isLoading: false,
			success: false,
			hasError: false,
			error: null,
			progress: 0,
			finished: false,
			cooldownSeconds: 0,
			severity: 'default'
		} as DeviceRebootResult
	},
	// STATES - In-progress or special states
	{
		id: 'loading',
		label: 'Rebooting',
		description: 'Reboot in progress',
		data: {
			isLoading: true,
			success: false,
			hasError: false,
			error: null,
			progress: 50,
			finished: false,
			cooldownSeconds: 0,
			severity: 'loading'
		} as DeviceRebootResult
	},
	{
		id: 'cooldown',
		label: 'Cooldown (Success)',
		description: 'In cooldown period after successful reboot',
		data: {
			isLoading: false,
			success: true,
			hasError: false,
			error: null,
			progress: 100,
			finished: true,
			cooldownSeconds: 30,
			severity: 'ok'
		} as DeviceRebootResult
	},
	{
		id: 'cooldown-error',
		label: 'Cooldown (Error)',
		description: 'In cooldown period after failed reboot',
		data: {
			isLoading: false,
			success: false,
			hasError: true,
			error: new Error('Reboot failed'),
			progress: 0,
			finished: true,
			cooldownSeconds: 30,
			severity: 'error'
		} as DeviceRebootResult
	}
]

/**
 * Get variant groups for device reboot testing
 * Organized into Results and States sections
 * Colors are automatically derived from severity using STATE_VARIANTS
 */
export function getDeviceRebootMockVariantGroups() {
	// Results section - completed reboot operations
	const resultMocks = ['success', 'error', 'ready']
	const results = DEVICE_REBOOT_MOCKS
		.filter(mock => resultMocks.includes(mock.id))
		.map(mock => {
			const variantKey = getSeverityStateVariant(mock.data.severity)
			const variant = STATE_VARIANTS[variantKey]

			return {
				id: mock.id,
				label: mock.label,
				colorClass: variant.colorClass
			}
		})

	// States section - in-progress or special states
	const stateMocks = ['loading', 'cooldown', 'cooldown-error']
	const states = DEVICE_REBOOT_MOCKS
		.filter(mock => stateMocks.includes(mock.id))
		.map(mock => {
			const variantKey = getSeverityStateVariant(mock.data.severity)
			const variant = STATE_VARIANTS[variantKey]

			return {
				id: mock.id,
				label: mock.label,
				colorClass: variant.colorClass
			}
		})

	return [
		{
			label: 'Results',
			variants: results
		},
		{
			label: 'States',
			variants: states
		}
	]
}
